import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import {
  useSaveCommand,
  useUpdateScreenChatContext,
} from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { JSONText } from '$tablet-app/src/app/developer/components/JSONText';
import { createGoalModel } from '$tablet-app/src/app/goals/functions';
import { postCreateRecordLogEvent } from '$tablet-app/src/app/logging/functions/postCreateRecordLogEvent';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import { useCurrentProfile } from '$tablet-app/src/app/users';
import { findAndUpdateRecord } from '$tablet-app/src/app/utils/arrays';
import { removeRecordByID } from '$tablet-app/src/app/utils/records';
import useBool from '$tablet-app/src/app/utils/state/useBool';
import { Alert, createGoalReminder } from '@akin-home/alerts';
import { useCreateAlertFromModel } from '@akin-home/alerts/frontend';
import { FeatureDomain } from '@akin-home/amplify';
import { Goal, getDefaultValuesFromGoalTemplate } from '@akin-home/goals';
import { useCreateGoal } from '@akin-home/goals/frontend';
import { useListGoalTemplates } from '@akin-home/goals/react';
import { showInfoToast, showSuccessToast } from '@akin-home/toast/native';
import React, { useCallback, useEffect, useState } from 'react';
import { useUpdateGoalFormCommand } from '../../hooks';
import GoalFormScreenView from '../GoalFormScreen/GoalFormScreenView';
import useGoalFormScreen from '../GoalFormScreen/useGoalFormScreen';
import { GoalSuggestionDialog } from '../GoalSuggestionDialog';

const context = {
  canSave: true,
  fields: { title: { type: 'text' }, period: {}, completions_per_period: {} },
};

const parameters = {
  defaults: {
    title: 'text',
    period: '"day", "week", "month" or "year"',
    completions_per_period: 'number',
  },
};

const GoalFormScreen = ({
  navigation: { goBack },
  route: { params },
}: RootStackScreenProps<'CreateGoal'>) => {
  useUpdateScreenChatContext(context);

  const { profile: user } = useCurrentProfile();
  const { id: user_id } = user;

  const { state, setGoal, updateGoal, toggleShowPeriods, toggleShowReminders } =
    useGoalFormScreen({
      goal: createGoalModel({
        participant_ids: [user.id],
        habitatID: user.habitatID,
        ...params?.defaults,
      }),
    });

  const [suggestions_visible, showSuggestions, hideSuggestions] = useBool();

  const { data: goals, isLoading } = useListGoalTemplates();

  /** Just first 3 */
  const goal_suggestions = goals?.slice(0, 3);

  useUpdateGoalFormCommand(updateGoal);

  const create = useCreateGoal();

  const [alerts, setAlerts] = useState<Alert[]>([]);

  const createAlert = useCreateAlertFromModel();

  const onSubmit = useSaveCommand(
    useCallback(async () => {
      if (!state.goal) return;

      showInfoToast({ text1: 'Saving Goal' });
      goBack();

      // Create the goal
      const {
        data: { createGoal },
      } = await create(state.goal);

      if (createGoal?.id) {
        // Create reminders
        await createReminders(createGoal, alerts);

        postCreateRecordLogEvent({
          user_id,
          feature_domain: FeatureDomain.GOALS,
          data: {
            id: createGoal?.id,
            model: 'Goal',
          },
        });

        sendBackgroundMessage(intent_labels.create_goal, {
          context: {
            goal: {
              id: createGoal.id,
              title: createGoal.title,
              period: createGoal.period,
              completions_per_period: createGoal.completions_per_period,
            },
          },
        });
      }

      showSuccessToast({ text1: 'Goal Created' });
    }, [state.goal, alerts, user_id])
  );

  const onPressSuggestion = (suggestion: Goal) => {
    hideSuggestions();
    updateGoal(getDefaultValuesFromGoalTemplate(suggestion));
  };

  const createReminders = (goal: { id: string }, reminders: Alert[]) =>
    Promise.all(
      reminders.map((reminder) => createAlert({ ...reminder, goalID: goal.id }))
    );

  /** Add a reminder */
  const onAddReminder = () =>
    state.goal && setAlerts([...alerts, createGoalReminder(state.goal)]);

  /** Update a reminder */
  const onUpdateReminder = (reminder: Alert) =>
    setAlerts(findAndUpdateRecord(alerts, reminder));

  /** Delete a reminder */
  const onDeleteReminder = ({ id }: Alert) =>
    setAlerts(removeRecordByID(alerts, id));

  useEffect(() => {
    sendBackgroundMessage(intent_labels.open_create_goal_screen);
  }, []);

  if (!state.goal) return null;

  return (
    <>
      <GoalFormScreenView
        goal={state.goal}
        reminders={alerts}
        period_visible={state.periods_visible}
        reminders_visible={state.reminders_visible}
        loadingSuggestions={isLoading}
        goal_suggestions={goal_suggestions}
        onChange={setGoal}
        onCancel={goBack}
        onConfirm={onSubmit}
        toggleShowPeriods={toggleShowPeriods}
        toggleShowReminders={toggleShowReminders}
        onAddReminder={onAddReminder}
        onUpdateReminder={onUpdateReminder}
        onDeleteReminder={onDeleteReminder}
        onPressSuggestion={onPressSuggestion}
        onPressViewSuggestions={showSuggestions}
        setReminders={setAlerts}
      />

      <GoalSuggestionDialog
        visible={suggestions_visible}
        onClose={hideSuggestions}
        goals={goals}
        onPressSuggestion={onPressSuggestion}
      />

      <DeveloperScreenInfo
        context={<JSONText>{context}</JSONText>}
        parameters={<JSONText>{parameters}</JSONText>}
      />
    </>
  );
};

export default GoalFormScreen;
